import {BN, web3} from "@project-serum/anchor";
import {
	createAssociatedTokenAccountInstruction,
	createCloseAccountInstruction,
	createHarvestWithheldTokensToMintInstruction,
	createSyncNativeInstruction,
	createTransferCheckedInstruction,
	getAssociatedTokenAddressSync, TOKEN_2022_PROGRAM_ID,
	TOKEN_PROGRAM_ID
} from "@solana/spl-token";
import {PoolConfig, TokenInput, TokenSwapLayout, TokenSwapPool} from "./layouts";
import {SWAP_PROGRAM_ID, WSOL} from "./constants";
import {CreateTokenPool} from "./create_token_pool";
import Instructions from "./instructions";

export default class Client {

	connection;

	poolTokenProgramId = TOKEN_PROGRAM_ID //The program ID of the token program for the pool tokens

	constructor(connection: web3.Connection) {
		this.connection = connection
	}


	async getPools() {
		const resp = await this.connection.getProgramAccounts(SWAP_PROGRAM_ID)
		return resp.map((m) => {
			return {pubkey: m.pubkey, account: TokenSwapLayout.decode(m.account.data)}
		})
	}

	/**
	 * @deprecated Use fluxbeam api /pools/search/mints (findPools)
	 */
	async getSwapPools(tokenA: web3.PublicKey, tokenB: web3.PublicKey) {
		const resp = await this.connection.getProgramAccounts(SWAP_PROGRAM_ID, {
			commitment: 'confirmed',
			filters: [
				{
					memcmp: {
						offset: 1 + 1 + 1 + 32 + 32 + 32 + 32,
						bytes: tokenA.toString(),
					},
				},
				{
					memcmp: {
						offset: 1 + 1 + 1 + 32 + 32 + 32 + 32 + 32,
						bytes: tokenB.toString(),
					},
				},
			],
		})
		const respInverse = await this.connection.getProgramAccounts(SWAP_PROGRAM_ID, {
			commitment: 'confirmed',
			filters: [
				{
					memcmp: {
						offset: 1 + 1 + 1 + 32 + 32 + 32 + 32,
						bytes: tokenB.toString(),
					},
				},
				{
					memcmp: {
						offset: 1 + 1 + 1 + 32 + 32 + 32 + 32 + 32,
						bytes: tokenA.toString(),
					},
				},
			],
		})
		return resp.concat(respInverse).map((m) => {
			return {pubkey: m.pubkey, account: TokenSwapLayout.decode(m.account.data)}
		})
	}


	async getSwapPoolsSingle(tokenA: web3.PublicKey) {
		const resp = await this.connection.getProgramAccounts(SWAP_PROGRAM_ID, {
			commitment: 'confirmed',
			filters: [
				{
					memcmp: {
						offset: 1 + 1 + 1 + 32 + 32 + 32 + 32,
						bytes: tokenA.toString(),
					},
				},
			],
		})
		const respInverse = await this.connection.getProgramAccounts(SWAP_PROGRAM_ID, {
			commitment: 'confirmed',
			filters: [
				{
					memcmp: {
						offset: 1 + 1 + 1 + 32 + 32 + 32 + 32 + 32,
						bytes: tokenA.toString(),
					},
				},
			],
		})
		return resp.concat(respInverse).map((m) => {
			return {pubkey: m.pubkey, account: TokenSwapLayout.decode(m.account.data)}
		})
	}


	async getPool(pool: web3.PublicKey) {
		const resp = await this.connection.getAccountInfo(pool, "confirmed")
		return {pubkey: pool, owner: resp?.owner, account: TokenSwapLayout.decode(resp!.data)}
	}

	async getPoolDetail(poolPK: web3.PublicKey, pool: TokenSwapPool, walletPk: web3.PublicKey) {
		const resp = await this.connection.getMultipleParsedAccounts([
			new web3.PublicKey(pool.tokenAccountA),
			new web3.PublicKey(pool.tokenAccountB),
			getAssociatedTokenAddressSync(new web3.PublicKey(pool.tokenPool), poolPK, true, TOKEN_PROGRAM_ID),
			getAssociatedTokenAddressSync(new web3.PublicKey(pool.tokenPool), walletPk, true, TOKEN_PROGRAM_ID),
			new web3.PublicKey(pool.mintA),
			new web3.PublicKey(pool.mintB),
			new web3.PublicKey(pool.tokenPool),
		])

		return {
			//@ts-ignore
			tokenAccountA: resp?.value[0]?.data?.parsed?.info,
			//@ts-ignore
			tokenAccountB: resp?.value[1]?.data?.parsed?.info,
			//@ts-ignore
			tokenPool: resp?.value[2]?.data?.parsed?.info,
			//@ts-ignore
			userLP: resp?.value[3]?.data?.parsed?.info,
			//@ts-ignore
			mintA: resp?.value[4]?.data?.parsed?.info,
			//@ts-ignore
			mintB: resp?.value[5]?.data?.parsed?.info,
			//@ts-ignore
			mintProgramA: resp?.value[4]?.owner,
			//@ts-ignore
			mintProgramB: resp?.value[5]?.owner,
			//@ts-ignore
			mintLp: resp?.value[6]?.data?.parsed?.info,
			poolAddress: poolPK,
		}
	}

	async createPoolTransactions(
		payer: web3.PublicKey,
		feeAccount: web3.PublicKey,
		tokenA: TokenInput,
		tokenB: TokenInput,
		config: PoolConfig,
	) {
		const cp = new CreateTokenPool(
			this.connection,
			payer,
			feeAccount,
			tokenA,
			tokenB,
			config
		)

		const initTxn = await cp.initializeTransaction()
		const createTxn = await cp.createTransaction()

		// return [
		// 	{
		// 		txn: initTxn.transaction,
		// 		signers: initTxn.signers
		// 	}, {
		// 		txn: createTxn.transaction,
		// 		signers: createTxn.signers
		// 	}
		// ]

		initTxn.transaction.add(...createTxn.transaction.instructions)
		initTxn.signers.push(...createTxn.signers)

		return [
			{
				pool: cp.tokenSwapAccount.publicKey,
				txn: initTxn.transaction,
				signers: initTxn.signers
			}
		]
	}

	async createSwapTransaction(
		payer: web3.PublicKey,
		pool: web3.PublicKey,
		srcMint: TokenInput,
		dstMint: TokenInput,
		route: TokenSwapPool,
		amountIn: number,
		minimumAmountOut: number,
		mintAInfo?: {
			value: {
				owner: web3.PublicKey;
			};
		}) {

		const aToB = srcMint.mint.equals(new web3.PublicKey(route.mintA))

		console.log("createSwapTransaction", {
			amountIn,
			minimumAmountOut,
			srcMint: srcMint.mint.toString(),
			dstMint: dstMint.mint.toString(),
			routeSrc: route.mintA.toString(),
			routeDst: route.mintB.toString(),
			aToBo: aToB
		})

		if (!mintAInfo || !mintAInfo.value) {
			mintAInfo = await this.connection.getParsedAccountInfo(srcMint.mint);
		}
		const mintBInfo = await this.connection.getParsedAccountInfo(dstMint.mint)

		console.log("mintAInfo", mintAInfo, srcMint.mint.toString());
		console.log("mintBInfo", mintBInfo, dstMint.mint.toString());

		// Check if we have valid token info before proceeding
		if (!mintAInfo?.value?.owner) {
			throw new Error(`Failed to retrieve source token information for ${srcMint.mint.toString()}`);
		}

		if (!mintBInfo?.value?.owner) {
			throw new Error(`Failed to retrieve destination token information for ${dstMint.mint.toString()}`);
		}

		const transaction = new web3.Transaction()

		const [authority] = web3.PublicKey.findProgramAddressSync([pool.toBuffer()], SWAP_PROGRAM_ID);

		const userSource = getAssociatedTokenAddressSync(srcMint.mint, payer, true, mintAInfo.value?.owner!)
		const userDestination = getAssociatedTokenAddressSync(dstMint.mint, payer, true, mintBInfo.value?.owner!)
		const userDestinationInfo = await this.connection.getParsedAccountInfo(userDestination)

		const poolSource = new web3.PublicKey(aToB ? route.tokenAccountA : route.tokenAccountB)
		const poolDestination = new web3.PublicKey(aToB ? route.tokenAccountB : route.tokenAccountA)


		if (srcMint.mint.equals(WSOL)) {
			//Do sync native checks
			const ixs = await this.getWrapSOLInstructions(payer, Math.ceil(srcMint.amount));
			if (ixs.length > 0)
				transaction.add(...ixs)
		}

		if (!userDestinationInfo.value) {
			transaction.add(createAssociatedTokenAccountInstruction(payer, userDestination, payer, dstMint.mint, mintBInfo.value?.owner!))
		}

		transaction.add(Instructions.createSwapInstruction(
			pool,
			authority,
			payer,
			userSource,
			poolSource,
			poolDestination,
			userDestination,
			new web3.PublicKey(route.tokenPool),
			new web3.PublicKey(route.feeAccount),
			new web3.PublicKey(route.feeAccount), //hostFeeAccount,
			srcMint.mint,
			dstMint.mint,
			SWAP_PROGRAM_ID,
			mintAInfo.value?.owner!,
			mintBInfo.value?.owner!,
			TOKEN_PROGRAM_ID,
			new BN(amountIn, 10),
			new BN(minimumAmountOut, 10),
		))

		if (dstMint.mint.equals(WSOL)) {
			//Do sync native checks
			transaction.add(this.getUnwrapSOLInstruction(payer))
		}


		if (this.hasTransferFeeConfig(mintAInfo))
			transaction.add(createHarvestWithheldTokensToMintInstruction(
				srcMint.mint,
				[
					userSource,
					poolSource
				]
			))

		if (this.hasTransferFeeConfig(mintBInfo))
			transaction.add(createHarvestWithheldTokensToMintInstruction(
				dstMint.mint,
				[
					userDestination,
					poolDestination
				]
			))

		return transaction
	}


	async createAddLiquidityTransaction(payer: web3.PublicKey, pool: web3.PublicKey, route: TokenSwapPool, srcMint: TokenInput, dstMint: TokenInput, poolTokenAmount: any, initTokenPrice = false, priorityFee) {
		const mintAInfo = await this.connection.getParsedAccountInfo(new web3.PublicKey(route.mintA))
		const mintBInfo = await this.connection.getParsedAccountInfo(new web3.PublicKey(route.mintB))
		const [authority] = web3.PublicKey.findProgramAddressSync([pool.toBuffer()], SWAP_PROGRAM_ID);

		const userAccountA = getAssociatedTokenAddressSync(new web3.PublicKey(route.mintA), payer, true, mintAInfo.value?.owner!)
		const userAccountB = getAssociatedTokenAddressSync(new web3.PublicKey(route.mintB), payer, true, mintBInfo.value?.owner!)
		const userPoolTokenAccount = getAssociatedTokenAddressSync(new web3.PublicKey(route.tokenPool), payer, true, TOKEN_PROGRAM_ID)

		const balanceInfo = await this.connection.getMultipleParsedAccounts([userPoolTokenAccount], {commitment: "confirmed"})
		const [userPoolTokenAccountInfo] = balanceInfo.value


		const unitLimit = 100_000
		const unitPrice = Math.floor(priorityFee / unitLimit)

		const transaction = new web3.Transaction().add(
			web3.ComputeBudgetProgram.setComputeUnitPrice({microLamports: unitPrice}),
		)

		if (new web3.PublicKey(route.mintA).equals(WSOL)) {
			//Do sync native checks
			const ixs = await this.getWrapSOLInstructions(payer, srcMint.amount);
			if (ixs.length > 0)
				transaction.add(...ixs)
		}

		if (new web3.PublicKey(route.mintB).equals(WSOL)) {
			//Do sync native checks
			const ixs = await this.getWrapSOLInstructions(payer, dstMint.amount);
			if (ixs.length > 0)
				transaction.add(...ixs)
		}

		if (!userPoolTokenAccountInfo)
			transaction.add(createAssociatedTokenAccountInstruction(payer, userPoolTokenAccount, payer, new web3.PublicKey(route.tokenPool), TOKEN_PROGRAM_ID))

		console.debug("depositAllTokenTypesInstruction", {
			pool,
			mintA: route.mintA,
			mintB: route.mintB,
			poolTokenAmount: poolTokenAmount,
			tokenAAmount: srcMint.amount,
			tokenBAmount: dstMint.amount,
			srcMint,
			dstMint,
		})

		if (initTokenPrice) {
			const ratio = dstMint.amount / srcMint.amount

			//ts-ignore
			poolTokenAmount = 1_000_000_000

			console.log("initTokenPrice", {
				poolTokenAmount: poolTokenAmount,
				tokenA: route.mintA.toString(),
				tokenB: route.mintB.toString(),
				ratio, //b to a
				amountA: srcMint.amount,
				amountB: dstMint.amount,
				eAmountA: srcMint.amount / 2,
				eAmountB: dstMint.amount / 2,
				exactAmountInA: srcMint.amountInExact(srcMint.amount / 2),
				exactAmountInB: dstMint.amountInExact(dstMint.amount / 2)
			})


			//@ts-ignore
			transaction.add(createTransferCheckedInstruction(userAccountA, new web3.PublicKey(route.mintA), new web3.PublicKey(route.tokenAccountA), payer, srcMint.amountInExact(srcMint.amount / 2), mintAInfo.value?.data?.parsed?.info.decimals, [], mintAInfo.value?.owner!))

			//@ts-ignore
			transaction.add(createTransferCheckedInstruction(userAccountB, new web3.PublicKey(route.mintB), new web3.PublicKey(route.tokenAccountB), payer, dstMint.amountInExact(dstMint.amount / 2), mintBInfo.value?.data?.parsed?.info.decimals, [], mintBInfo.value?.owner!))

			// srcMint.amount -= srcMint.exactAmountIn(srcMint.amount/2)
			// dstMint.amount -= dstMint.exactAmountIn(dstMint.amount/2)
			srcMint.amount = srcMint.amount / 2
			dstMint.amount = dstMint.amount / 2
		}

		transaction.add(Instructions.depositAllTokenTypesInstruction(
			pool,
			authority,
			payer,
			userAccountA,
			userAccountB,
			new web3.PublicKey(route.tokenAccountA),
			new web3.PublicKey(route.tokenAccountB),
			new web3.PublicKey(route.tokenPool),
			userPoolTokenAccount,
			new web3.PublicKey(route.mintA),
			new web3.PublicKey(route.mintB),
			SWAP_PROGRAM_ID,
			mintAInfo.value?.owner!,
			mintBInfo.value?.owner!,
			TOKEN_PROGRAM_ID,
			new BN(poolTokenAmount, 10),
			new BN(srcMint.amount, 10),
			new BN(dstMint.amount, 10),
		))

		if (new web3.PublicKey(route.mintB).equals(WSOL)) {
			//Do sync native checks
			transaction.add(await this.getUnwrapSOLInstruction(payer))
		}

		return transaction
	}

	async createAddSingleSideLiquidityTransaction(payer: web3.PublicKey, pool: web3.PublicKey, route: TokenSwapPool, srcMint: TokenInput, minPoolTokenAmount: BN, priorityFee) {
		const [authority] = web3.PublicKey.findProgramAddressSync([pool.toBuffer()], SWAP_PROGRAM_ID);

		const userAccount = getAssociatedTokenAddressSync(srcMint.mint, payer, true, srcMint.programID)
		const userPoolTokenAccount = getAssociatedTokenAddressSync(new web3.PublicKey(route.tokenPool), payer, true, TOKEN_PROGRAM_ID)

		const balanceInfo = await this.connection.getMultipleParsedAccounts([userPoolTokenAccount], {commitment: "confirmed"})
		const [userPoolTokenAccountInfo] = balanceInfo.value


		const unitLimit = 200_000
		const unitPrice = Math.floor(priorityFee / unitLimit)

		const transaction = new web3.Transaction().add(
			web3.ComputeBudgetProgram.setComputeUnitPrice({microLamports: unitPrice}),
		)

		if (srcMint.mint.equals(WSOL)) {
			//Do sync native checks
			const ixs = await this.getWrapSOLInstructions(payer, srcMint.amount);
			if (ixs.length > 0)
				transaction.add(...ixs)
		}

		if (!userPoolTokenAccountInfo)
			transaction.add(createAssociatedTokenAccountInstruction(payer, userPoolTokenAccount, payer, new web3.PublicKey(route.tokenPool), TOKEN_PROGRAM_ID))


		console.log("amountIn", srcMint.amount)
		console.log("amountOut", minPoolTokenAmount)

		transaction.add(Instructions.depositSingleTokenTypeExactAmountInInstruction(
			pool,
			authority,
			payer,
			userAccount,
			new web3.PublicKey(route.tokenAccountA),
			new web3.PublicKey(route.tokenAccountB),
			new web3.PublicKey(route.tokenPool),
			userPoolTokenAccount,
			srcMint.mint,
			SWAP_PROGRAM_ID,
			srcMint.programID,
			TOKEN_PROGRAM_ID,
			new BN(srcMint.amount),
			new BN(minPoolTokenAmount)
		))

		if (srcMint.mint.equals(WSOL)) {
			//Do sync native checks
			transaction.add(this.getUnwrapSOLInstruction(payer))
		}

		return transaction
	}

	async createRemoveLiquidityTransaction(payer: web3.PublicKey, pool: web3.PublicKey, route: TokenSwapPool, poolTokenAmount: number, minimumTokenA: number, minimumTokenB: number, priorityFee: number) {
		const mintAInfo = await this.connection.getParsedAccountInfo(new web3.PublicKey(route.mintA))
		const mintBInfo = await this.connection.getParsedAccountInfo(new web3.PublicKey(route.mintB))
		const [authority] = web3.PublicKey.findProgramAddressSync([pool.toBuffer()], SWAP_PROGRAM_ID);

		const userAccountA = getAssociatedTokenAddressSync(new web3.PublicKey(route.mintA), payer, true, mintAInfo.value?.owner!)
		const userAccountB = getAssociatedTokenAddressSync(new web3.PublicKey(route.mintB), payer, true, mintBInfo.value?.owner!)
		const userPoolTokenAccount = getAssociatedTokenAddressSync(new web3.PublicKey(route.tokenPool), payer, true, TOKEN_PROGRAM_ID)

		const balanceInfo = await this.connection.getMultipleParsedAccounts([userAccountA, userAccountB, new web3.PublicKey(route.tokenAccountA), new web3.PublicKey(route.tokenAccountB), userPoolTokenAccount])

		const [uAInfo, uBInfo, tAInfo, tBInfo, spInfo] = balanceInfo.value

		console.log({
			//@ts-ignore
			userAccountAAmount: uAInfo?.data.parsed.info.tokenAmount.amount,
			//@ts-ignore
			userAccountBAmount: uBInfo?.data.parsed.info.tokenAmount.amount,
			//@ts-ignore
			tokenAccountAAmount: tAInfo?.data.parsed.info.tokenAmount.amount,
			//@ts-ignore
			tokenAccountBAmount: tBInfo?.data.parsed.info.tokenAmount.amount,
			//@ts-ignore
			sourcePoolAccountAmount: spInfo?.data.parsed.info.tokenAmount.amount,
		})


		const unitLimit = 200_000
		const unitPrice = Math.floor(priorityFee / unitLimit)

		const transaction = new web3.Transaction().add(
			web3.ComputeBudgetProgram.setComputeUnitPrice({microLamports: unitPrice}),
		)
		// deposit_all_token_types  deposit_single_token_type_exact_amount_in

		//Create wSOL account
		if (new web3.PublicKey(route.mintA).equals(WSOL) || new web3.PublicKey(route.mintB).equals(WSOL)) {
			const ixs = await this.getWrapSOLInstructions(payer, 0);
			if (ixs.length > 0)
				transaction.add(...ixs)
		}

		if (!uAInfo?.data && !new web3.PublicKey(route.mintA).equals(WSOL)) {
			transaction.add(createAssociatedTokenAccountInstruction(payer, userAccountA, payer, new web3.PublicKey(route.mintA), mintAInfo.value?.owner!))
		}

		if (!uBInfo?.data && !new web3.PublicKey(route.mintB).equals(WSOL)) {
			transaction.add(createAssociatedTokenAccountInstruction(payer, userAccountB, payer, new web3.PublicKey(route.mintB), mintBInfo.value?.owner!))
		}


		transaction.add(Instructions.withdrawAllTokenTypesInstruction(
			pool,
			authority,
			payer,
			new web3.PublicKey(route.tokenPool),
			new web3.PublicKey(route.feeAccount),
			userPoolTokenAccount,
			new web3.PublicKey(route.tokenAccountA),
			new web3.PublicKey(route.tokenAccountB),
			userAccountA,
			userAccountB,
			new web3.PublicKey(route.mintA),
			new web3.PublicKey(route.mintB),
			SWAP_PROGRAM_ID,
			TOKEN_PROGRAM_ID,
			mintAInfo.value?.owner!,
			mintBInfo.value?.owner!,
			poolTokenAmount,
			minimumTokenA,
			minimumTokenB,
		))

		//Unwrap sol
		if (new web3.PublicKey(route.mintA).equals(WSOL) || new web3.PublicKey(route.mintB).equals(WSOL))
			transaction.add(this.getUnwrapSOLInstruction(payer))

		return transaction
	}

	//TODO Test
	async createRemoveSingleSideLiquidityTransaction(payer: web3.PublicKey, pool: web3.PublicKey, route: TokenSwapPool, dstMint: TokenInput, poolTokenAmount: number, priorityFee: number) {
		const mintInfo = await this.connection.getParsedAccountInfo(dstMint.mint)
		const [authority] = web3.PublicKey.findProgramAddressSync([pool.toBuffer()], SWAP_PROGRAM_ID);

		const userAccount = getAssociatedTokenAddressSync(dstMint.mint, payer, true, mintInfo.value?.owner!)

		const userPoolTokenAccount = getAssociatedTokenAddressSync(new web3.PublicKey(route.tokenPool), payer, true, TOKEN_PROGRAM_ID)

		const balanceInfo = await this.connection.getMultipleParsedAccounts([userAccount, new web3.PublicKey(route.tokenAccountA), new web3.PublicKey(route.tokenAccountB), userPoolTokenAccount])

		const [uAInfo, tAInfo, tBInfo, spInfo] = balanceInfo.value

		console.log({
			//@ts-ignore
			userAccountAAmount: uAInfo?.data.parsed.info.tokenAmount.amount,
			//@ts-ignore
			tokenAccountAAmount: tAInfo?.data.parsed.info.tokenAmount.amount,
			//@ts-ignore
			tokenAccountBAmount: tBInfo?.data.parsed.info.tokenAmount.amount,
			//@ts-ignore
			sourcePoolAccountAmount: spInfo?.data.parsed.info.tokenAmount.amount,
		})


		const unitLimit = 200_000
		const unitPrice = Math.floor(priorityFee / unitLimit)

		const transaction = new web3.Transaction().add(
			web3.ComputeBudgetProgram.setComputeUnitPrice({microLamports: unitPrice}),
		)

		//Create wSOL account
		if (dstMint.mint.equals(WSOL)) {
			const ixs = await this.getWrapSOLInstructions(payer, 0);
			if (ixs.length > 0)
				transaction.add(...ixs)
		}

		if (!uAInfo?.data && !dstMint.mint.equals(WSOL)) {
			transaction.add(createAssociatedTokenAccountInstruction(payer, userAccount, payer, new web3.PublicKey(route.mintB), mintInfo.value?.owner!))
		}

		transaction.add(Instructions.withdrawSingleTokenTypeExactAmountOutInstruction(
			pool,
			authority,
			payer,
			new web3.PublicKey(route.tokenPool),
			new web3.PublicKey(route.feeAccount),
			userPoolTokenAccount,
			new web3.PublicKey(route.tokenAccountA),
			new web3.PublicKey(route.tokenAccountB),
			userAccount,
			dstMint.mint,
			SWAP_PROGRAM_ID,
			TOKEN_PROGRAM_ID,
			mintInfo.value?.owner!,
			new BN(dstMint.amount, 10),
			new BN(poolTokenAmount),
		))

		//Unwrap sol
		if (dstMint.mint.equals(WSOL))
			transaction.add(this.getUnwrapSOLInstruction(payer))

		return transaction
	}


	async getWrapSOLInstructions(owner: web3.PublicKey, amount: number): Promise<web3.TransactionInstruction[]> {
		const ixs: web3.TransactionInstruction[] = []
		const ata = getAssociatedTokenAddressSync(WSOL, owner, true)
		const ataInfo = await this.connection.getTokenAccountBalance(ata).catch(() => {
		})

		if (ataInfo) {
			if (Number(ataInfo?.value.amount) >= amount)
				return ixs;
		}

		if (!ataInfo) {
			ixs.push(createAssociatedTokenAccountInstruction(owner, ata, owner, WSOL))
		}
		if (amount > 0)
			ixs.push(...[
				web3.SystemProgram.transfer({
					fromPubkey: owner,
					toPubkey: ata,
					lamports: amount - Number(ataInfo?.value.amount || 0),
				}),
				createSyncNativeInstruction(ata)
			])

		return ixs
	}

	getUnwrapSOLInstruction(owner: web3.PublicKey): web3.TransactionInstruction {
		const ata = getAssociatedTokenAddressSync(WSOL, owner, true)
		return createCloseAccountInstruction(ata, owner, owner)
	}

	hasTransferFeeConfig(mintInfo: any): boolean {
		if(!mintInfo.value) return false
		if (!TOKEN_2022_PROGRAM_ID.equals(mintInfo.value?.owner))
			return false

		return mintInfo.value?.data.parsed?.info.extensions?.filter((ex: any) => ex.extension === "transferFeeConfig").length > 0
	}
}